import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

/* fetch clients */
export const fetchClients = createAsyncThunk(
  "clients",
  async ( rejectWithValue ) => {
    try {
      const response = await apiPath.get('/api/v1.0/clients');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* create client */
export const createClient = createAsyncThunk('client/create',async (clientData, { rejectWithValue }) => {
  try {
    const response = await apiPath.post(`/api/v1.0/clients/create`, clientData);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* update client */
export const updateClient = createAsyncThunk('client/update',async ({ id, clientData }, { rejectWithValue }) => {
  try {
    const response = await apiPath.put(`/api/v1.0/clients/${id}`, clientData);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* client detail */
export const clientDetail = createAsyncThunk('client/detail',async ( id, { rejectWithValue }) => {
  try {
    const response = await apiPath.get(`/api/v1.0/clients/${id}`);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* delete client */
export const deleteClient = createAsyncThunk('client/delete',async ( id, { rejectWithValue }) => {
  try {
    await apiPath.delete(`/api/v1.0/clients/${id}`);
    return id;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* fetch client Lists */
export const clientLists = createAsyncThunk('client/list',async ( rejectWithValue ) => {
  try {
    const response = await apiPath.get('/api/v1.0/clients/lists');
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* create client settings */
export const createClientSettings = createAsyncThunk('client/settings/create', async (settingsData, { rejectWithValue }) => {
  try {
    const response = await apiPath.post('/api/v1.0/client-settings/create', settingsData);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* fetch client settings lists */
export const fetchClientSettingsLists = createAsyncThunk('client/settings/lists', async ({ client_id, type }, { rejectWithValue }) => {
  try {
    let url = `/api/v1.0/client-settings/lists?client_id=${client_id}`;
    if (type) {
      url += `&type=${type}`;
    }
    const response = await apiPath.get(url);
    return response.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

/* Client Slice @start */
const clientSlice = createSlice({
  name: 'client',
  initialState: {
    clients: [],
    loading: false,
    clientDetail: {},
    clientSettings: [],
    dashboardSettings: [],
    settingsLoading: false,
    error: null,
    settingsError: null,
  },
  reducers: {},
    extraReducers: (builder) => {
      builder
      /* Fetch Clients */
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.clients = action.payload.clients;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      /* Create Client */
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.push(action.payload); 
        state.loading = false;
        state.error = null;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      /* Update Client */
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        const index = state.clients.findIndex(
          client => client.id === action.payload.id
        );
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      /* Delete Client */
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter(
          (client) => client.id !== action.payload
        );
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      /* Client Details */
      .addCase(clientDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clientDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.clientDetail = action.payload.details;
      })
      .addCase(clientDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      /* Client Lists */
      .addCase(clientLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clientLists.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.clients = action.payload;
        state.lists = action.payload.clients;
      })
      .addCase(clientLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      /* Create Client Settings */
      .addCase(createClientSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(createClientSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = null;
        if (action.payload && action.payload.length > 0 && action.payload[0]?.type === 'dashboard') {
          state.dashboardSettings = action.payload;
          state.clientSettings = [];
        } else {
          state.clientSettings = action.payload;
          state.dashboardSettings = [];
        }
      })
      .addCase(createClientSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message;
      })

      /* Fetch Client Settings Lists */
      .addCase(fetchClientSettingsLists.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(fetchClientSettingsLists.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = null;
        if (action.meta.arg?.type === 'dashboard') {
          state.dashboardSettings = action.payload;
        } else if (action.meta.arg?.type === 'report_permissions') {
          state.clientSettings = action.payload;
        } else {
          state.clientSettings = action.payload;
        }
      })
      .addCase(fetchClientSettingsLists.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.error.message;
      });
    },
});

export default clientSlice.reducer;
